import BaseScene from '@Scenes/BaseScene';
import AddSuffix from '@Utils/Number/AddSuffix';

/*--------------- Config ------------------------*/
import * as Scenes from '@Config/scenes';
import { LeaderboardIDs } from '@Config/leaderboards';

/*---------------- Core ------------------------*/
import Game from '@Game';
import Network from '@Networks/Localhost';
import AnalyticsEvents from '@Config/analytics_events';
import CreateScoreImage from '@Utils/Image/CreateScoreImage';
import Delay from '@Utils/Time/Delay';


/**
 * Score Scene,
 * Displays score and tricks
 */
class EndScoreScene extends BaseScene
{
    private globalRankValueText: Phaser.GameObjects.Text;
    private globalRankLoadingCircle: Phaser.GameObjects.Graphics;

    private weeklyRankValueText: Phaser.GameObjects.Text;
    private weeklyRankLoadingCircle: Phaser.GameObjects.Graphics;

    private bestScoreValueText: Phaser.GameObjects.Text;
    private lifetimeBestDistanceValueText: Phaser.GameObjects.Text;

    private UI_OFFSET_Y: number = 50;

    /**
     * Contructor
     */
    constructor()
    {
        super(Scenes.END_SCORE);
    }

    /**
     * Create
     */
    public async create()
    {
        this.createSceneUI();

        await this.submitScoreAsync();
    }

    /**
     * Creates scene elements
     */
    public createSceneUI()
    {
        const fontStyle = { fontFamily: 'Permanent Marker' };

        this.createScoreUI(fontStyle);

        if (Network.getSupportedAPIs().indexOf('getLeaderboardAsync') !== -1)
        {
            this.createRankUI(fontStyle);
        }
        else
        {
            this.createBestScoreUI(fontStyle);
        }
    }

    public createScoreUI(fontStyle)
    {
        const gameSize = this.getGameSize();

        const offsetY = this.UI_OFFSET_Y;

        const connectedPlayerScore = Game.runScore;

        const distance = connectedPlayerScore.getDistanceScore();
        const trickScore = connectedPlayerScore.getTrickScore();
        const scoreTotal = connectedPlayerScore.getTotalScore();

        const titleText = this.add.text(gameSize.centerX, 50 + offsetY, 'Your Score', fontStyle);
        titleText.setOrigin(0.5);
        titleText.setFontSize(40);
        titleText.setAlign('center');
        titleText.setColor('#000000');
        titleText.alpha = 0;

        const distanceLabelText = this.add.text(175, 135 + offsetY, 'Distance', fontStyle);
        distanceLabelText.setOrigin(0);
        distanceLabelText.setFontSize(30);
        distanceLabelText.setColor('#777777');
        distanceLabelText.setAlign('left');
        distanceLabelText.alpha = 0;

        const distanceValueText = this.add.text(gameSize.width - 175, 135 + offsetY, distance + 'm', fontStyle);
        distanceValueText.setOrigin(1, 0);
        distanceValueText.setFontSize(30);
        distanceValueText.setColor('#777777');
        distanceValueText.setAlign('right');
        distanceValueText.alpha = 0;

        const trickScoreLabelText = this.add.text(175, 196 + offsetY, 'Tricks', fontStyle);
        trickScoreLabelText.setOrigin(0);
        trickScoreLabelText.setFontSize(30);
        trickScoreLabelText.setAlign('left');
        trickScoreLabelText.setColor('#777777');
        trickScoreLabelText.alpha = 0;

        const trickScoreValueText = this.add.text(gameSize.width - 175, 175 + offsetY, trickScore + '', fontStyle);
        trickScoreValueText.setOrigin(1, 0);
        trickScoreValueText.setFontSize(30);
        trickScoreValueText.setAlign('right');
        trickScoreValueText.setColor('#777777');
        trickScoreValueText.alpha = 0;

        const totalLabelText = this.add.text(175, 225 + offsetY, 'Total', fontStyle);
        totalLabelText.setOrigin(0);
        totalLabelText.setFontSize(40);
        totalLabelText.setAlign('left');
        totalLabelText.setColor('#000000')
        totalLabelText.alpha = 0;

        const totalValueText = this.add.text(gameSize.width - 175, 225 + offsetY, scoreTotal + '', fontStyle);
        totalValueText.setOrigin(1, 0);
        totalValueText.setFontSize(40);
        totalValueText.setAlign('right');
        totalValueText.setColor('#000000');
        totalValueText.alpha = 0;

        this.add.tween({ targets: titleText, alpha: 1, duration: 500 });
        this.add.tween({ targets: [distanceLabelText, distanceValueText], alpha: 1, y: 100 + offsetY, duration: 500, delay: 500, ease: 'Quad.easeOut' });
        this.add.tween({ targets: [trickScoreLabelText, trickScoreValueText], alpha: 1, y: 155 + offsetY, duration: 500, delay: 750, ease: 'Quad.easeOut' });
        this.add.tween({ targets: [totalLabelText, totalValueText], alpha: 1, y: 210 + offsetY, duration: 500, delay: 1000, ease: 'Quad.easeOut' });
    }

    /**
     * Creates Rank UI
     */
    public createRankUI(fontStyle)
    {
        const gameSize = this.getGameSize();

        const offsetY = this.UI_OFFSET_Y;

        const rankingsTitle = this.add.text(gameSize.centerX, 55, 'Rankings', fontStyle);
        rankingsTitle.setOrigin(0.5);
        rankingsTitle.setFontSize(25);
        rankingsTitle.setColor('#000000');

        const weeklyRankLabelText = this.add.text(175, 75, 'Weekly', fontStyle);
        weeklyRankLabelText.setOrigin(0);
        weeklyRankLabelText.setFontSize(20);
        weeklyRankLabelText.setAlign('left');
        weeklyRankLabelText.setColor('#000000')

        const weeklyRankValueText = this.add.text(gameSize.width - 175, 75, '', fontStyle);
        weeklyRankValueText.setOrigin(1, 0);
        weeklyRankValueText.setFontSize(20);
        weeklyRankValueText.setAlign('right');
        weeklyRankValueText.setColor('#000000')

        this.weeklyRankValueText = weeklyRankValueText;

        const globalRankLabelText = this.add.text(175, 115, 'Global', fontStyle);
        globalRankLabelText.setOrigin(0);
        globalRankLabelText.setFontSize(20);
        globalRankLabelText.setAlign('left');
        globalRankLabelText.setColor('#000000')

        const globalRankValueText = this.add.text(gameSize.width - 175, 115, '', fontStyle);
        globalRankValueText.setOrigin(1, 0);
        globalRankValueText.setFontSize(20);
        globalRankValueText.setAlign('right');
        globalRankValueText.setColor('#000000')

        this.globalRankValueText = globalRankValueText;

        const globalRankLoadingCircle = this.add.graphics();
        globalRankLoadingCircle.lineStyle(2, 0x000000);
        globalRankLoadingCircle.beginPath();
        globalRankLoadingCircle.arc(0, 0, 10, 0, (Math.PI * 2) * 0.85);
        globalRankLoadingCircle.strokePath();
        globalRankLoadingCircle.x = gameSize.width - 190;
        globalRankLoadingCircle.y = 125;

        this.globalRankLoadingCircle = globalRankLoadingCircle;

        const weeklyRankLoadingCircle = this.add.graphics();
        weeklyRankLoadingCircle.lineStyle(2, 0x000000);
        weeklyRankLoadingCircle.beginPath();
        weeklyRankLoadingCircle.arc(0, 0, 10, 0, (Math.PI * 2) * 0.85);
        weeklyRankLoadingCircle.strokePath();
        weeklyRankLoadingCircle.x = gameSize.width - 190;
        weeklyRankLoadingCircle.y = 90;

        this.weeklyRankLoadingCircle = weeklyRankLoadingCircle;

        const rankingsContainer = this.add.container(0, 290);
        rankingsContainer.add(rankingsTitle);
        rankingsContainer.add(weeklyRankLabelText);
        rankingsContainer.add(weeklyRankValueText);
        rankingsContainer.add(globalRankLabelText);
        rankingsContainer.add(globalRankValueText);
        rankingsContainer.add(globalRankLoadingCircle);
        rankingsContainer.add(weeklyRankLoadingCircle);
        rankingsContainer.alpha = 0;

        this.add.tween({ targets: rankingsContainer, alpha: 1, y: 220 + offsetY, delay: 500, duration: 500 });
    }

    /**
     * Creates Rank UI
     */
    public createBestScoreUI(fontStyle)
    {
        const gameSize = this.getGameSize();

        const offsetY = this.UI_OFFSET_Y;

        const rankingsTitle = this.add.text(gameSize.centerX, 55, 'Best Score', fontStyle);
        rankingsTitle.setOrigin(0.5);
        rankingsTitle.setFontSize(25);
        rankingsTitle.setColor('#000000');

        const bestScoreValue = this.add.text(gameSize.centerX, 100, 'Best Score', fontStyle);
        bestScoreValue.setOrigin(0.5);
        bestScoreValue.setFontSize(40);
        bestScoreValue.setColor('#000000');

        this.bestScoreValueText = bestScoreValue;

        const bestScoresContainer = this.add.container(0, 290);
        bestScoresContainer.add(rankingsTitle);
        bestScoresContainer.add(bestScoreValue);

        // bestScoresContainer.add(lifetimeBestDistanceValue);
        // bestScoresContainer.add(lifetimeBestDistanceValueText);

        bestScoresContainer.alpha = 0;

        this.add.tween({ targets: bestScoresContainer, alpha: 1, y: 220 + offsetY, delay: 500, duration: 500 });
    }

    /**
     * Update callback, fires on every tick
     */
    public update()
    {
        if (this.globalRankLoadingCircle)
        {
            this.globalRankLoadingCircle.angle += 10;
        }

        if (this.weeklyRankLoadingCircle)
        {
            this.weeklyRankLoadingCircle.angle += 10;
        }
    }

    /**
    * Submits score
    */
    private async submitScoreAsync()
    {
        const playerScore = Game.runScore;

        /**
         *
         */
        const distanceScore = playerScore.getDistanceScore();
        const trickScore = playerScore.getTrickScore();
        const totalScore = playerScore.getTotalScore();

        /**
         * Score Details Json
         */
        const scoreDetailsJSON = JSON.stringify({
            distance: distanceScore,
            tricks: trickScore
        });

        /**
         * Set session score internally
         */
        Game.sessionScore.setDistanceScore(distanceScore);
        Game.sessionScore.setTrickScore(trickScore);

        /**
         * Set lifetimes score
         */
        Game.lifetimeScore.setDistanceScore(distanceScore);
        Game.lifetimeScore.setTrickScore(trickScore);

        /**
         * Update storage of best score
         * Note this is separate than leaderboard scoring
         */
        Network.player.setDataAsync({
            bestScore: {
                distance: Game.lifetimeScore.getDistanceScore(),
                tricks: Game.lifetimeScore.getTrickScore()
            }
        });

        /**
         * Submit scores to network
         */

        if (Network.getSupportedAPIs().indexOf('getLeaderboardAsync') !== -1)
        {
            await this.submitToWeeklyLeaderboard(totalScore, scoreDetailsJSON);

            await this.updateWeeklyRankText();

            await Delay(500);

            await this.submitToGlobalLeaderboard(totalScore, scoreDetailsJSON);

            await this.updateGlobalRankText();

            await Delay(500);

            await this.submitToContextualLeaderboard(totalScore, scoreDetailsJSON);
        }
        else
        {
            this.updateBestScoreText();
        }

        /**
         * Send update message
         */
        await this.sendUpdateMessage({
            action: 'CUSTOM',
            cta: "Play Now!",
            template: 'challenge',
            strategy: 'LAST'
        });

    }


    /**
     * Submit score to global leaderboard
     */
    public async submitToGlobalLeaderboard(totalScore, scoreDetailsJSON)
    {
        const globalLeaderboard = Game.leaderboards[LeaderboardIDs.GLOBAL];

        try
        {
            await globalLeaderboard.setScoreAsync(totalScore, scoreDetailsJSON);
        }
        catch (error)
        {
            Network.logEvent(AnalyticsEvents.FAILED_TO_SUBMIT_SCORE, null, { error_code: error.code, non_interaction: 'true' });
        }

    }

    /**
     * Submits score to weekly leaderboard
     */
    public async submitToWeeklyLeaderboard(totalScore, scoreDetailsJSON)
    {
        const weeklyLeaderboard = Game.leaderboards[LeaderboardIDs.WEEKLY];

        try
        {
            await weeklyLeaderboard.setScoreAsync(totalScore, scoreDetailsJSON);
        }
        catch (error)
        {
            Network.logEvent(AnalyticsEvents.FAILED_TO_SUBMIT_SCORE, null, { error_code: error.code, non_interaction: 'true' });
        }

    }

    /**
     * Submits score to contextual leaderboard
     */
    public async submitToContextualLeaderboard(totalScore, scoreDetailsJSON)
    {
        const contextualLeaderboard = Game.leaderboards[LeaderboardIDs.CONTEXTUAL];

        try
        {
            await contextualLeaderboard.setScoreAsync(totalScore, scoreDetailsJSON);
        }
        catch (error)
        {
            Network.logEvent(AnalyticsEvents.FAILED_TO_SUBMIT_SCORE, null, { error_code: error.code, non_interaction: 'true' });
        }

    }

    /**
     * Send update message to network.
     * This results in a message posted in the context they are playing in
     */
    private sendUpdateMessage(messageObj)
    {
        if (Network.getSupportedAPIs().indexOf('updateAsync') !== -1)
        {
            /**
             * Create splash image
             */
            const shareSplashTexture = this.textures.get('splash');
            const shareSplashImage = shareSplashTexture.getSourceImage() as HTMLImageElement;
            const bestSessionScore = Game.sessionScore.getTotalScore();
            const scoreSplashImage = CreateScoreImage(shareSplashImage, bestSessionScore);
            messageObj.image = scoreSplashImage;

            /**
             * Set message text
             */
            messageObj.text = `${Network.player.getName()} just scored ${bestSessionScore} in Free Rider Jumps!`;

            Network.updateAsync(messageObj).catch((error) =>
            {
                Network.logEvent(AnalyticsEvents.FAILED_TO_SEND_UPDATE_MESSAGE, null, { error_code: error.code, non_interaction: 'true' });
            });
        }
    }

    /**
     * Updates global rank text
     */
    private async updateGlobalRankText()
    {
        try
        {
            const globalPlayerEntry = await Game.leaderboards[LeaderboardIDs.GLOBAL].getPlayerEntryAsync();

            this.globalRankValueText.setText(AddSuffix(globalPlayerEntry.getRank()));
        }
        catch (error)
        {
            this.globalRankValueText.setText('--');

            Network.logEvent(AnalyticsEvents.FAILED_TO_GET_PLAYER_LEADERBOARD_ENTRY, null, { error_code: error.code, non_interaction: 'true' });
        }

        this.globalRankLoadingCircle.setVisible(false);
    }


    /**
    * Updates weekly rank text
    */
    private async updateWeeklyRankText()
    {
        try
        {
            const weeklyPlayerEntry = await Game.leaderboards[LeaderboardIDs.WEEKLY].getPlayerEntryAsync();

            this.weeklyRankValueText.setText(AddSuffix(weeklyPlayerEntry.getRank()));
        }
        catch (error)
        {
            this.weeklyRankValueText.setText('--');

            Network.logEvent(AnalyticsEvents.FAILED_TO_GET_PLAYER_LEADERBOARD_ENTRY, null, { error_code: error.code, non_interaction: 'true' });
        }

        this.weeklyRankLoadingCircle.setVisible(false);
    }

    /**
     * Updates best score text
     */
    private updateBestScoreText()
    {
        const lifetimeScore = Game.lifetimeScore;

        this.bestScoreValueText.setText(lifetimeScore.getTotalScore().toString());
    }
}

export default EndScoreScene;